import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  CssBaseline,
  Paper,
  Typography
} from '@mui/material'
import GoogleOauthButton from '../UI/GoogleOauthButton'
import anearApi from '../Api/AnearApi'
import UserAuthContainer from '../Containers/UserAuthContainer'
import useNavigation from '../Utils/navigationUtils'
import ROUTES from '../../routesConfig'
import AuthTokenExpiredError from '../Api/AuthTokenExpiredError'
import { CONTENT_COLUMN_MAX_WIDTH, contentColumnPageShellSx } from '../UI/contentColumn'

const invitationRoleLabels = (invitation) => {
  if (!invitation) return []
  const names = invitation['event-role-names']
  if (Array.isArray(names) && names.length > 0) {
    return names.map((name) => String(name || '').trim()).filter(Boolean)
  }
  const roles = invitation['event-roles']
  if (Array.isArray(roles) && roles.length > 0) {
    return roles
      .map((role) => role?.name || role?.slug || '')
      .map((s) => String(s || '').trim())
      .filter(Boolean)
  }
  return []
}

const invitationStatusCopy = (effectiveState) => {
  switch (String(effectiveState || '').toLowerCase()) {
    case 'superseded':
      return 'This invitation link has been replaced by a newer host invite. Please use the most recent email from the merchant.'
    case 'revoked':
      return 'This host invitation has been revoked by the merchant. You no longer have event host access from this link.'
    case 'expired':
      return 'This host invitation has expired. Ask the merchant to resend the latest host invite.'
    default:
      return ''
  }
}

const EventHostInvitePage = () => {
  const userAuth = UserAuthContainer.useContainer()
  const {
    navigateToLogin,
    navigateToSignup,
    navigateToVenue,
    handleAuthTokenExpired
  } = useNavigation()

  const [invitation, setInvitation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [accepting, setAccepting] = useState(false)
  const guestClearedRef = useRef(false)
  const autoRedirectedRef = useRef(false)

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), [])
  const token = searchParams.get('token') || ''
  const returnTarget = `${ROUTES.EVENT_HOST_INVITE}?token=${encodeURIComponent(token)}`

  useEffect(() => {
    if (guestClearedRef.current) return
    if (userAuth.isGuestAuthed()) {
      guestClearedRef.current = true
      userAuth.clearUserAndToken()
    }
  }, [userAuth])

  useEffect(() => {
    const bootstrapAuthedUser = async () => {
      if (!userAuth.hasAuthToken() || userAuth.isAuthed()) return
      try {
        await userAuth.loadUser(true)
      } catch (err) {
        if (err instanceof AuthTokenExpiredError) {
          handleAuthTokenExpired(err, userAuth)
        }
      }
    }

    bootstrapAuthedUser()
  }, [handleAuthTokenExpired, userAuth])

  useEffect(() => {
    let cancelled = false

    const loadInvitation = async () => {
      if (!token) {
        setError(new Error('This invitation link is missing a token.'))
        setLoading(false)
        return
      }

      try {
        const attrs = await anearApi.getEventHostInvitation(token)
        if (!cancelled) setInvitation(attrs)
      } catch (err) {
        if (!cancelled) setError(err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadInvitation()
    return () => { cancelled = true }
  }, [token])

  const handleAcceptInvitation = useCallback(async () => {
    if (!invitation || !userAuth.isRegisteredAuthed() || !token) return
    setAccepting(true)
    setError(null)

    try {
      const acceptedInvitation = await anearApi.acceptEventHostInvitation(token)
      setInvitation(acceptedInvitation)
    } catch (err) {
      if (err instanceof AuthTokenExpiredError) {
        handleAuthTokenExpired(err, userAuth)
        return
      }
      setError(err)
    } finally {
      setAccepting(false)
    }
  }, [handleAuthTokenExpired, invitation, token, userAuth])

  const goToVenue = useCallback(() => {
    const slug = invitation?.['venue-slug']
    if (slug) navigateToVenue(slug)
  }, [invitation, navigateToVenue])

  const goToLogin = async () => {
    if (userAuth.isGuestAuthed()) {
      userAuth.clearUserAndToken()
    }
    navigateToLogin({ pathname: ROUTES.EVENT_HOST_INVITE, search: `?token=${encodeURIComponent(token)}` })
  }

  const goToSignup = async () => {
    if (userAuth.isGuestAuthed()) {
      userAuth.clearUserAndToken()
    }
    navigateToSignup({ pathname: ROUTES.EVENT_HOST_INVITE, search: `?token=${encodeURIComponent(token)}` })
  }

  const venueName = invitation?.['venue-name'] || 'Venue'
  const appName = invitation?.['app-long-name'] || invitation?.['app-short-name'] || 'app'
  const roleLabels = invitationRoleLabels(invitation)
  const effectiveState = invitation?.['effective-state'] || ''
  const statusCopy = invitationStatusCopy(effectiveState)
  const isAccepted = effectiveState === 'accepted'
  const isSuperseded = effectiveState === 'superseded'
  const isRevoked = effectiveState === 'revoked'
  const isExpired = effectiveState === 'expired'
  const isInactiveInvitation = isSuperseded || isRevoked || isExpired
  const showRegisteredInvite = !loading && invitation && userAuth.isRegisteredAuthed()
  const canAccept = showRegisteredInvite && !isAccepted && !isInactiveInvitation && !accepting
  const acceptedVenueSlug = invitation?.['venue-slug'] || ''

  // Once an invitation is in the accepted state and we have a venue slug, send
  // the host straight to the consumer venue page so they see their assigned-app
  // tile without an extra "Go to venue" tap. The ref guard ensures we navigate
  // exactly once even though the navigateToVenue closure is re-created per render.
  useEffect(() => {
    if (autoRedirectedRef.current) return
    if (!showRegisteredInvite || !isAccepted || !acceptedVenueSlug) return
    autoRedirectedRef.current = true
    navigateToVenue(acceptedVenueSlug)
  }, [acceptedVenueSlug, isAccepted, navigateToVenue, showRegisteredInvite])

  return (
    <Box sx={contentColumnPageShellSx}>
      <CssBaseline />
      <Paper
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 3,
          px: 3,
          pb: 3,
          bgcolor: '#0B0F19',
          color: '#E6E9EF',
          borderRadius: 3,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: CONTENT_COLUMN_MAX_WIDTH
        }}
      >
        <Typography component="h1" variant="h5" sx={{ color: '#E6E9EF', fontWeight: 800, letterSpacing: 0.2 }}>
          Event host invite
        </Typography>

        {loading && (
          <Typography variant="body1" sx={{ mt: 2, color: 'rgba(230,233,239,0.82)', textAlign: 'center' }}>
            Loading your invitation...
          </Typography>
        )}

        {!loading && invitation && (
          <>
            <Typography
              variant="body1"
              sx={{ mt: 2, color: 'rgba(230,233,239,0.92)', textAlign: 'center', maxWidth: '30rem' }}
            >
              {invitation.email} has been invited to help host <strong>{venueName}</strong>.
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1, color: 'rgba(148,163,184,0.95)', textAlign: 'center', maxWidth: '30rem' }}
            >
              Assigned app: {appName}
            </Typography>
            {roleLabels.length > 0 && (
              <Box sx={{ mt: 1.5, width: '100%', maxWidth: '30rem' }}>
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(148,163,184,0.95)', fontWeight: 700, textAlign: 'center', mb: 0.75 }}
                >
                  Assigned roles
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'rgba(230,233,239,0.9)' }}>
                  {roleLabels.map((label) => (
                    <Typography key={label} component="li" variant="body2" sx={{ mb: 0.35 }}>
                      {label}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            {isInactiveInvitation && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  width: '100%',
                  bgcolor: 'rgba(251, 191, 36, 0.1)',
                  border: '1px solid rgba(251, 191, 36, 0.3)'
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: '#FDE68A', fontWeight: 700, lineHeight: 1.5, textAlign: 'center' }}
                >
                  {statusCopy}
                </Typography>
              </Box>
            )}
          </>
        )}

        {!loading && accepting && (
          <Typography variant="body1" sx={{ mt: 2, color: 'rgba(230,233,239,0.82)', textAlign: 'center' }}>
            Accepting your host access now...
          </Typography>
        )}

        {!loading && error && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              width: '100%',
              bgcolor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)'
            }}
          >
            <Typography variant="body2" sx={{ color: '#EF4444', fontWeight: 500, lineHeight: 1.5, textAlign: 'center' }}>
              {error.message || 'This invitation could not be completed.'}
            </Typography>
          </Box>
        )}

        {showRegisteredInvite && isAccepted && !accepting && (
          <>
            <Typography
              variant="body1"
              sx={{ mt: 2, color: 'rgba(230,233,239,0.92)', textAlign: 'center', maxWidth: '30rem' }}
            >
              You have accepted host access for <strong>{venueName}</strong>.
            </Typography>
            {acceptedVenueSlug ? (
              <Typography
                variant="body2"
                sx={{ mt: 1.5, color: 'rgba(148,163,184,0.95)', textAlign: 'center' }}
              >
                Taking you to the venue...
              </Typography>
            ) : (
              <Button
                sx={{
                  mt: 2.5,
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: 999,
                  px: 3,
                  py: 1.25,
                  fontSize: '1rem',
                  background: 'linear-gradient(90deg,#7C3AED,#06B6D4)',
                  '&:hover': {
                    background: 'linear-gradient(90deg,#6D28D9,#0891B2)'
                  }
                }}
                fullWidth
                variant="contained"
                onClick={goToVenue}
              >
                Go to venue
              </Button>
            )}
          </>
        )}

        {canAccept && (
          <>
            <Typography
              variant="body2"
              sx={{ mt: 2, color: 'rgba(148,163,184,0.95)', textAlign: 'center', maxWidth: '30rem' }}
            >
              Event host access requires a full Anear account. Confirm below to accept this invitation.
            </Typography>
            <Button
              sx={{
                mt: 2,
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: 999,
                px: 3,
                py: 1.5,
                fontSize: '1.05rem',
                background: 'linear-gradient(90deg,#7C3AED,#06B6D4)',
                boxShadow: '0 0 32px rgba(124,58,237,0.45)',
                '&:hover': {
                  background: 'linear-gradient(90deg,#6D28D9,#0891B2)',
                  boxShadow: '0 0 40px rgba(124,58,237,0.55)'
                }
              }}
              fullWidth
              variant="contained"
              onClick={handleAcceptInvitation}
            >
              Accept invitation
            </Button>
            <Button
              sx={{
                mt: 1.25,
                color: '#94A3B8',
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600
              }}
              size="small"
              onClick={() => userAuth.clearUserAndToken()}
            >
              Not now (sign out)
            </Button>
          </>
        )}

        {!loading && invitation && !accepting && !userAuth.isRegisteredAuthed() && !isInactiveInvitation && (
          <>
            <Typography
              variant="body2"
              sx={{ mt: 2, color: 'rgba(148,163,184,0.95)', textAlign: 'center', maxWidth: '30rem' }}
            >
              Event host access requires a full Anear account. Sign in or create one, then return here to review and accept the invite.
            </Typography>

            <Button
              sx={{
                mt: 3,
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: 999,
                px: 3,
                py: 1.5,
                fontSize: '1.1rem',
                background: 'linear-gradient(90deg,#7C3AED,#06B6D4)',
                boxShadow: '0 0 32px rgba(124,58,237,0.45)',
                '&:hover': {
                  background: 'linear-gradient(90deg,#6D28D9,#0891B2)',
                  boxShadow: '0 0 40px rgba(124,58,237,0.55)'
                }
              }}
              fullWidth
              variant="contained"
              onClick={goToLogin}
            >
              Log in
            </Button>

            <Button
              sx={{
                mt: 1.5,
                color: '#A78BFA',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 700
              }}
              size="small"
              onClick={goToSignup}
            >
              Sign up with email
            </Button>

            <GoogleOauthButton
              label="Continue with Google"
              returnTarget={returnTarget}
            />
          </>
        )}
      </Paper>
    </Box>
  )
}

export default EventHostInvitePage
